import React, { useState, useEffect, useRef } from 'react';
import './AdvancedFilters.css';
import PriceRangeSlider from './PriceRangeSlider';
import { formatPrice } from '../utils/shopifyDataTransformers';
import { trackComparisonFilterSelected } from '../helpers/analytics';

// Define filter options
interface FilterOption {
  id: string;
  label: string;
}

// Brand filter options
const brandOptions: FilterOption[] = [
  { id: 'brand_apple', label: 'iPhone' },
  { id: 'brand_samsung', label: 'Galaxy' },
  { id: 'brand_google', label: 'Pixel' }
];

// Phone size filter options
const sizeOptions: FilterOption[] = [
  { id: 'size_small', label: 'Small' },
  { id: 'size_medium', label: 'Medium' },
  { id: 'size_large', label: 'Large' }
];

// Homebutton filter options
const homeButtonOptions: FilterOption[] = [
  { id: 'home_all', label: 'All Types' },
  { id: 'home_yes', label: 'Only Homebutton phones' },
  { id: 'home_no', label: 'Only Swipe phones' }
];

interface AdvancedFiltersProps {
  onFilterChange: (filterType: string, value: string) => void;
}

const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({
  onFilterChange
}) => {
  // Refs for dropdowns
  const priceDropdownRef = useRef<HTMLDivElement>(null);
  const brandDropdownRef = useRef<HTMLDivElement>(null);
  const sizeDropdownRef = useRef<HTMLDivElement>(null);
  const homeButtonDropdownRef = useRef<HTMLDivElement>(null);

  // States for dropdown open/closed
  const [isPriceOpen, setIsPriceOpen] = useState<boolean>(false);
  const [isBrandOpen, setIsBrandOpen] = useState<boolean>(false);
  const [isSizeOpen, setIsSizeOpen] = useState<boolean>(false);
  const [isHomeButtonOpen, setIsHomeButtonOpen] = useState<boolean>(false);
  
  // States for filter values
  const [minPriceValue, setMinPriceValue] = useState<number>(0);
  const [maxPriceValue, setMaxPriceValue] = useState<number>(1200);
  const [brandValues, setBrandValues] = useState<string[]>([]);
  const [sizeValues, setSizeValues] = useState<string[]>([]);
  const [homeButtonValue, setHomeButtonValue] = useState<string>('home_all');

  // Effect for price filter
  useEffect(() => {
    // Format the price range as min-max
    onFilterChange('price', `${minPriceValue}-${maxPriceValue}`);
  }, [minPriceValue, maxPriceValue, onFilterChange]);

  // Effect for brand filter
  useEffect(() => {
    if (brandValues.length === 0) {
      onFilterChange('brand', 'brand_all');
    } else {
      onFilterChange('brand', brandValues.join(','));
    }
  }, [brandValues, onFilterChange]);

  // Effect for size filter
  useEffect(() => {
    if (sizeValues.length === 0) {
      onFilterChange('size', 'size_all');
    } else {
      onFilterChange('size', sizeValues.join(','));
    }
  }, [sizeValues, onFilterChange]);

  // Effect for home button filter
  useEffect(() => {
    onFilterChange('home_button', homeButtonValue);
  }, [homeButtonValue, onFilterChange]);

  // Effect to handle click outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Price dropdown
      if (priceDropdownRef.current && !priceDropdownRef.current.contains(event.target as Node)) {
        setIsPriceOpen(false);
      }
      
      // Brand dropdown
      if (brandDropdownRef.current && !brandDropdownRef.current.contains(event.target as Node)) {
        setIsBrandOpen(false);
      }
      
      // Size dropdown
      if (sizeDropdownRef.current && !sizeDropdownRef.current.contains(event.target as Node)) {
        setIsSizeOpen(false);
      }
      
      // Home button dropdown
      if (homeButtonDropdownRef.current && !homeButtonDropdownRef.current.contains(event.target as Node)) {
        setIsHomeButtonOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle checkbox change for filters
  const handleCheckboxChange = (
    id: string, 
    checked: boolean, 
    setValues: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    if (checked) {
      setValues(prev => [...prev, id]);
    } else {
      setValues(prev => prev.filter(value => value !== id));
    }
  };
  
  // Handle size checkbox change
  const handleSizeChange = (sizeId: string, checked: boolean) => {
    handleCheckboxChange(sizeId, checked, setSizeValues);
    trackComparisonFilterSelected('size', sizeId);
  };
  
  // Handle brand checkbox change
  const handleBrandChange = (brandId: string, checked: boolean) => {
    handleCheckboxChange(brandId, checked, setBrandValues);
    trackComparisonFilterSelected('brand', brandId);
  };

  // Get price display text
  const getPriceDisplayText = () => {
    if (minPriceValue === 0 && maxPriceValue === 1200) return 'All Prices';
    return `$${formatPrice(minPriceValue)} - $${formatPrice(maxPriceValue)}`;
  };

  // Get brand display text
  const getBrandDisplayText = () => {
    if (brandValues.length === 0) return 'All Brands';
    if (brandValues.length === 1) {
      const option = brandOptions.find(opt => opt.id === brandValues[0]);
      return option ? option.label : 'Selected Brand';
    }
    return `${brandValues.length} Brands Selected`;
  };

  // Get size display text
  const getSizeDisplayText = () => {
    if (sizeValues.length === 0) return 'All Sizes';
    if (sizeValues.length === 1) {
      const option = sizeOptions.find(opt => opt.id === sizeValues[0]);
      return option ? option.label : 'Selected Size';
    }
    return `${sizeValues.length} Sizes Selected`;
  };

  // Get home button display text
  const getHomeButtonDisplayText = () => {
    const option = homeButtonOptions.find(opt => opt.id === homeButtonValue);
    return option ? option.label : 'All Types';
  };

  // Arrow SVG component
  const ArrowIcon = ({ isOpen }: { isOpen: boolean }) => (
    <svg
      className={`dropdown-arrow ${isOpen ? 'open' : ''}`}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M7 10L12 15L17 10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  // Visibility is now handled by the parent component

  return (
    <div className="advanced-filters-container">
      {/* Price Range Filter */}
      <div className="filter-group">
        <h3 className="filter-title">Price Range</h3>
        <div 
          className={`filter-dropdown ${isPriceOpen ? 'open' : ''}`}
          ref={priceDropdownRef}
        >
          <button 
            className="filter-dropdown-button"
            onClick={() => {
              if (!isPriceOpen) {
                trackComparisonFilterSelected('price', 'dropdown_opened');
              }
              setIsPriceOpen(!isPriceOpen);
            }}
            aria-expanded={isPriceOpen}
          >
            <span className="filter-dropdown-label">{getPriceDisplayText()}</span>
            <ArrowIcon isOpen={isPriceOpen} />
          </button>
          
          {isPriceOpen && (
            <div className="filter-dropdown-content">
              <div className="range-filter">
                <PriceRangeSlider
                  min={0}
                  max={1200}
                  step={10}
                  minValue={minPriceValue}
                  maxValue={maxPriceValue}
                  onChange={(min, max) => {
                    setMinPriceValue(min);
                    setMaxPriceValue(max);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Brand Filter */}
      <div className="filter-group">
        <h3 className="filter-title">Brand</h3>
        <div 
          className={`filter-dropdown ${isBrandOpen ? 'open' : ''}`}
          ref={brandDropdownRef}
        >
          <button 
            className="filter-dropdown-button"
            onClick={() => setIsBrandOpen(!isBrandOpen)}
            aria-expanded={isBrandOpen}
          >
            <span className="filter-dropdown-label">{getBrandDisplayText()}</span>
            <ArrowIcon isOpen={isBrandOpen} />
          </button>
          
          {isBrandOpen && (
            <div className="filter-dropdown-content">
              <div className="checkbox-options">
                {brandOptions.map(option => (
                  <div key={option.id} className="checkbox-option">
                    <input
                      type="checkbox"
                      id={option.id}
                      value={option.id}
                      checked={brandValues.includes(option.id)}
                      onChange={(e) => handleBrandChange(option.id, e.target.checked)}
                    />
                    <label htmlFor={option.id}>{option.label}</label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Phone Size Filter */}
      <div className="filter-group">
        <h3 className="filter-title">Phone Size</h3>
        <div 
          className={`filter-dropdown ${isSizeOpen ? 'open' : ''}`}
          ref={sizeDropdownRef}
        >
          <button 
            className="filter-dropdown-button"
            onClick={() => setIsSizeOpen(!isSizeOpen)}
            aria-expanded={isSizeOpen}
          >
            <span className="filter-dropdown-label">{getSizeDisplayText()}</span>
            <ArrowIcon isOpen={isSizeOpen} />
          </button>
          
          {isSizeOpen && (
            <div className="filter-dropdown-content">
              <div className="checkbox-options">
                {sizeOptions.map(option => (
                  <div key={option.id} className="checkbox-option">
                    <input
                      type="checkbox"
                      id={option.id}
                      value={option.id}
                      checked={sizeValues.includes(option.id)}
                      onChange={(e) => handleSizeChange(option.id, e.target.checked)}
                    />
                    <label htmlFor={option.id}>{option.label}</label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Homebutton Filter */}
      <div className="filter-group">
        <h3 className="filter-title">Homebutton</h3>
        <div 
          className={`filter-dropdown ${isHomeButtonOpen ? 'open' : ''}`}
          ref={homeButtonDropdownRef}
        >
          <button 
            className="filter-dropdown-button"
            onClick={() => setIsHomeButtonOpen(!isHomeButtonOpen)}
            aria-expanded={isHomeButtonOpen}
          >
            <span className="filter-dropdown-label">{getHomeButtonDisplayText()}</span>
            <ArrowIcon isOpen={isHomeButtonOpen} />
          </button>
          
          {isHomeButtonOpen && (
            <div className="filter-dropdown-content">
              <div className="radio-options">
                {homeButtonOptions.map(option => (
                  <div key={option.id} className="radio-option">
                    <input
                      type="radio"
                      id={option.id}
                      name="home_button"
                      value={option.id}
                      checked={homeButtonValue === option.id}
                      onChange={() => {
                        setHomeButtonValue(option.id);
                        trackComparisonFilterSelected('home_button', option.id);
                      }}
                    />
                    <label htmlFor={option.id}>{option.label}</label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdvancedFilters;